import { RootState } from "@store/index";
import Table from "@components/table";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Product, Category } from "@apis/index";
import { deletesvg, edit, eye, switchOff, switchOn } from "@assets/icons";
import { useNavigate } from "react-router-dom";
import Svg from "@components/svg";
import { useModal } from "@context/ModalContext";
import DeleteModal from "@components/delete-modal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import RecommendationModal from "./RecommendationModal";
import { Sparkles, Tag, X } from "lucide-react";
import { Helper } from "@utils/index";
import { useQueryState } from "@hooks/useQueryState";

const ProductList = ({
  storeId,
  moduleId,
  moduleName,
}: {
  storeId: any;
  moduleId: any;
  moduleName: string;
}) => {
  const { settings } = useGeneralSettings();
  const { data: user } = useSelector((state: RootState) => state.user);
  console.log({ user });
  const { bucketUrl } = useConfig();
  const navigate = useNavigate();
  const {
    items: products,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.products);
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");

  // Filter states
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [categories, setCategories] = useState<any[]>([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const categoryDropdownRef = React.useRef<HTMLDivElement>(null);

  // Fetch categories for filtering
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res: any = await Category.list({
          params: { limit: 1000, moduleId },
        });
        if (res?.items) setCategories(res.items);
        else if (Array.isArray(res)) setCategories(res);
      } catch (e) {}
    };
    fetchCategories();
  }, [moduleId]);

  // Handle outside click for dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchProducts = async () => {
    const params: any = {
      page,
      limit: 30,
      storeId: user.role === "store" ? user.id : storeId,
      search,
    };
    if (selectedCategoryId) params.categoryId = selectedCategoryId;

    await Product.list({
      state: "products",
      params,
    });
  };

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
  }, [search, selectedCategoryId]);

  useEffect(() => {
    fetchProducts();
  }, [page, search, selectedCategoryId]);

  const selectedCategory = categories.find(
    (c: any) => String(c.id) === selectedCategoryId,
  );
  const filteredCategories = categories.filter((c: any) =>
    (c.name || "").toLowerCase().includes(categorySearch.toLowerCase()),
  );

  const getRandomColor = (name: string) => {
    const colors = [
      "#F56565",
      "#ED8936",
      "#ECC94B",
      "#48BB78",
      "#38B2AC",
      "#4299E1",
      "#667EEA",
      "#9F7AEA",
      "#ED64A6",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name: string) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "?";
  };

  const columns: any = [
    {
      title: "SN",
      key: "sn",
      render: (_, i) => i + (page - 1) * 30,
    },

    {
      title: "Product",
      key: "product",
      render: (product: any) => {
        const imageUrl = product.image
          ? `${bucketUrl}/${product.image}`
          : product.images?.[0]?.image
            ? `${bucketUrl}/${product.images[0].image}`
            : null;

        return (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "flex-start",
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={product.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "8px",
                    backgroundColor: getRandomColor(product.name || "Product"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "16px",
                    flexShrink: 0,
                  }}
                >
                  {getInitials(product.name || "Product")}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333",
                    lineHeight: "1.2",
                  }}
                >
                  {product.name}
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {product.category?.name && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#C05621",
                        background: "#FEEBC8",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      {product.category.name}
                    </span>
                  )}
                  {product.subCategories?.map((s: any, idx: number) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: "10px",
                        color: "#555",
                        background: "#f0f0f0",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {s.name}
                    </span>
                  ))}
                  {product.brand?.name && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#2B6CB0",
                        background: "#BEE3F8",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      {product.brand.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Store",
      key: "store",
      render: (product: any) => {
        const storeName =
          product.store?.name || product.store?.storeName || "Store";
        const storeImage =
          product.store?.avatar || product.store?.logo
            ? `${bucketUrl}/${product.store?.avatar || product.store?.logo}`
            : null;

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (product.store?.userId) {
                navigate(
                  `/admin/users/store/store-lists/view/${product.store.userId}`,
                );
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
            }}
          >
            {storeImage ? (
              <img
                src={storeImage}
                alt={storeName}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "4px", // Made square (slight radius)
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "4px", // Made square (slight radius)
                  backgroundColor: getRandomColor(storeName),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "14px",
                  flexShrink: 0,
                }}
              >
                {getInitials(storeName)}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{ fontWeight: 500, fontSize: "14px", color: "#333" }}
              >
                {storeName}
              </span>
              <span style={{ fontSize: "12px", color: "#777" }}>
                {product.store?.phoneNo || product.store?.storeNumber || "-"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Stock",
      key: "stock",
      render: (product: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {product.stockInfos ? (
            <div
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor:
                  product.stockInfos.type === "unlimited"
                    ? "#e8f5e9" // Light green for unlimited
                    : product.stockInfos.stock !== undefined &&
                        product.stockInfos.stock !== null
                      ? product.stockInfos.stock > 10
                        ? "#e8f5e9" // Green for high stock
                        : product.stockInfos.stock > 0
                          ? "#fff3e0" // Orange for low stock
                          : "#ffebee" // Red for out of stock
                      : "#f5f5f5", // Gray for N/A
                color:
                  product.stockInfos.type === "unlimited"
                    ? "#2e7d32" // Dark green text
                    : product.stockInfos.stock !== undefined &&
                        product.stockInfos.stock !== null
                      ? product.stockInfos.stock > 10
                        ? "#2e7d32" // Dark green text
                        : product.stockInfos.stock > 0
                          ? "#e65100" // Dark orange text
                          : "#c62828" // Dark red text
                      : "#757575", // Gray text for N/A
                border: "1px solid",
                borderColor:
                  product.stockInfos.type === "unlimited"
                    ? "#c8e6c9" // Light green border
                    : product.stockInfos.stock !== undefined &&
                        product.stockInfos.stock !== null
                      ? product.stockInfos.stock > 10
                        ? "#c8e6c9" // Light green border
                        : product.stockInfos.stock > 0
                          ? "#ffe0b2" // Light orange border
                          : "#ffcdd2" // Light red border
                      : "#e0e0e0", // Light gray border
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "60px",
              }}
            >
              {product.stockInfos.type === "unlimited"
                ? "Unlimited"
                : product.stockInfos.stock !== undefined &&
                    product.stockInfos.stock !== null
                  ? `${product.stockInfos.stock}${product.stockInfos.type === "daily" ? " (Daily)" : ""}`
                  : "N/A"}
            </div>
          ) : (
            <div
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor: "#f5f5f5",
                color: "#999",
                border: "1px solid #e0e0e0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "60px",
              }}
            >
              -
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Delivery",
      key: "delivery",
      render: (product: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {product.deliveryAvailability ? (
            <span
              style={{
                fontSize: "12px",
                color: "#333",
              }}
            >
              {product.deliveryAvailability}
              {product.deliveryAvailability === "regular" &&
              product.estimatedDeliveryDay
                ? ` (${product.estimatedDeliveryDay}d)`
                : ""}
            </span>
          ) : (
            <span style={{ fontSize: "12px", color: "#999" }}>-</span>
          )}
        </div>
      ),
    },
    {
      title: `Price (${settings.currencySymbol})`,
      key: "price",
      render: (product: any) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <span style={{ fontWeight: 500 }}>
            {product.actualAmount || product.price}
          </span>
          {product.actualAmount && product.actualAmount !== product.price && (
            <span
              style={{
                fontSize: "11px",
                color: "#999",
                textDecoration: "line-through",
              }}
            >
              {product.price}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Most Popular",
      key: "mostPopular",
      render: (item) => (
        <Svg
          src={item.isPopular ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isPopular ? "false" : "true";
            formData.append("isPopular", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },
    {
      title: "is Most Ordered",
      key: "isMostOrdered",
      render: (item) => (
        <Svg
          src={item.isMostOrdered ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isMostOrdered ? "false" : "true";
            formData.append("isMostOrdered", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },
    {
      title: "is Latest Arrival",
      key: "isLatestArrival",
      render: (item) => (
        <Svg
          src={item.isLatestArrival ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isLatestArrival ? "false" : "true";
            formData.append("isLatestArrival", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },

    {
      title: "Active",
      key: "isActive",
      render: (item: any) => (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          <Svg
            src={item.isActive ? switchOn : switchOff}
            onClick={async () => {
              const formData = new FormData();
              const payload = item.isActive ? "false" : "true";
              formData.append("isActive", payload);
              Product.update({
                state: "products",
                id: item.id,
                data: formData,
              }).then(() => {
                fetchProducts();
              });
            }}
          />
        </div>
      ),
    },
    {
      title: "Recommendations",
      key: "recommendations",
      render: (item: any) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
            showModal(
              <RecommendationModal
                product={item}
                visible={true}
                onClose={hideModal}
              />,
              { width: 1200 },
            );
          }}
          style={{
            cursor: "pointer",
            color: "#805ad5",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          title="Manage Recommendations"
        >
          <Sparkles size={18} />
        </div>
      ),
    },
  ];
  const { showModal, hideModal } = useModal();
  return (
    <div>
      {/* Filter Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 16px",
          marginBottom: "0",
          flexWrap: "wrap",
        }}
      >
        {/* Category Filter */}
        <div
          ref={categoryDropdownRef}
          style={{ position: "relative", minWidth: "200px" }}
        >
          <div
            onClick={() => {
              setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              border: selectedCategoryId
                ? "1.5px solid #DD6B20"
                : "1px solid #E2E8F0",
              borderRadius: "8px",
              cursor: "pointer",
              background: selectedCategoryId ? "#FFFAF0" : "#fff",
              fontSize: "13px",
              color: selectedCategoryId ? "#C05621" : "#4A5568",
              fontWeight: selectedCategoryId ? 600 : 400,
              transition: "all 0.2s ease",
              minHeight: "36px",
            }}
          >
            <Tag size={15} style={{ flexShrink: 0, opacity: 0.7 }} />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedCategory ? selectedCategory.name : "All Categories"}
            </span>
            {selectedCategoryId ? (
              <X
                size={14}
                style={{ flexShrink: 0, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCategoryId("");
                  setCategorySearch("");
                }}
              />
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                style={{ flexShrink: 0, opacity: 0.5 }}
              >
                <path
                  d="M3 5L6 8L9 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
          {isCategoryDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                zIndex: 100,
                maxHeight: "280px",
                display: "flex",
                flexDirection: "column",
                minWidth: "240px",
              }}
            >
              <div style={{ padding: "8px" }}>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "7px 10px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    fontSize: "13px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ overflowY: "auto", maxHeight: "220px" }}>
                <div
                  onClick={() => {
                    setSelectedCategoryId("");
                    setIsCategoryDropdownOpen(false);
                    setCategorySearch("");
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: !selectedCategoryId ? "#DD6B20" : "#4A5568",
                    fontWeight: !selectedCategoryId ? 600 : 400,
                    background: !selectedCategoryId ? "#FFFAF0" : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategoryId)
                      (e.target as HTMLElement).style.background = "#F7FAFC";
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategoryId)
                      (e.target as HTMLElement).style.background =
                        "transparent";
                  }}
                >
                  All Categories
                </div>
                {filteredCategories.map((cat: any) => {
                  const isActive = selectedCategoryId === String(cat.id);
                  return (
                    <div
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategoryId(String(cat.id));
                        setIsCategoryDropdownOpen(false);
                        setCategorySearch("");
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: isActive ? "#DD6B20" : "#4A5568",
                        fontWeight: isActive ? 600 : 400,
                        background: isActive ? "#FFFAF0" : "transparent",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          (e.currentTarget as HTMLElement).style.background =
                            "#F7FAFC";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive)
                          (e.currentTarget as HTMLElement).style.background =
                            "transparent";
                      }}
                    >
                      {cat.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <Table
        text="Product List"
        columns={columns}
        data={products || []}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
        onSearchChange={setSearch}
        itemsPerPage={30}
        actions={[
          {
            key: "view",
            icon: eye,
            onClick: (item) => {
              navigate(
                user.role === "store"
                  ? `/store/product-management/view-product/${item.id}?module=${moduleName}`
                  : `/admin/users/store/update-product/${item.id}?module=${moduleName}`,
                {
                  state: { productData: { ...item, moduleId } },
                },
              );
            },
          },
          ...(user.role !== "store" &&
          user.module?.name?.toLowerCase() !== "food"
            ? [
                {
                  key: "edit",
                  icon: edit,
                  onClick: (item: any) => {
                    navigate(
                      user.role === "store"
                        ? `/store/product-management/update-product/${item.id}?module=${moduleName}`
                        : `/admin/users/store/update-product/${item.id}?module=${moduleName}`,
                      {
                        state: { productData: { ...item, moduleId } },
                      },
                    );
                  },
                },
              ]
            : []),

          {
            key: "delete",
            icon: deletesvg,
            onClick: async (item) => {
              showModal(
                <DeleteModal
                  state="products"
                  api={Product}
                  id={item.id}
                  onSuccess={() => fetchProducts()}
                />,
              );
            },
          },
        ]}
      />
    </div>
  );
};

export default ProductList;
